import React, { useEffect, useState, createContext, useContext, PropsWithChildren, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { CartItem, SerializedCartItem, TokenData, UserLogin, WebData } from './ClassesPublic';
import Toast from 'react-hot-toast';
import API from './APIPublic';
import moment from 'moment';

var TitleContext = createContext<{ setTitle: (title: string, suffix?: boolean)=>void }>({ setTitle: (v: string)=>null });
var useTitle = () => useContext(TitleContext);

interface TitleProps extends PropsWithChildren{
	default?: string,
	suffix?: string,
}

var TitleProvider = (props: TitleProps) => {
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string, suffix: boolean }[]>([]);
	var location = useLocation();

	useEffect(()=>{
		var suffix = props.suffix || '';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		if(!title){
			document.title = suffix;
		}else{
			document.title = title.suffix ? `${title.title} - ${suffix}` : title.title;
		}
	}, [location, titleHistory]);

	var changeTitle = useCallback((title: string, suffix: boolean=true)=>{
		if(!location.key) return;
		setTitleHistory(prev => {
			var hs = [...prev];
			hs = hs.filter(a=>a.pathname!==location.pathname);
			hs.push({ pathname: location.pathname, title, suffix: suffix!==false });
			return hs;
		});
	}, [location.pathname, location.key]);

	var contextValue = useMemo(()=>({ setTitle: changeTitle }), [changeTitle]);

	return (
		<TitleContext.Provider value={contextValue}>
			{props.children}
		</TitleContext.Provider>
	)
}

interface CartContextProps{
	cart: CartItem[],
	serializedCart: SerializedCartItem[],
	items: number,
	loading: boolean,
	showing: boolean,
	total: number,
	showCart: ()=>void,
	hideCart: ()=>void,
	addToCart: (product_id: number, quantity: number, show_cart?: boolean)=>Promise<any>,
	addRedeemableToCart: (product: { product_id: number, name: string, redeem_points: number })=>void,
	removeFromCart: (product_id: number)=>void,
	updateCart: (product_id: number, quantity: number)=>Promise<any>,
	clearCart: ()=>void,
	clearRedeemables: ()=>void,
}

var CartContext = createContext<CartContextProps>({
	cart: [],
	serializedCart: [],
	items: 0,
	loading: false,
	showing: false,
	total: 0,
	showCart: ()=>null,
	hideCart: ()=>null,
	addToCart: ()=>null,
	addRedeemableToCart: ()=>null,
	removeFromCart: ()=>null,
	updateCart: ()=>null,
	clearCart: ()=>null,
	clearRedeemables: ()=>null,
});

var useCart = () => useContext(CartContext);

interface CartProps extends PropsWithChildren{}

var CartProvider = (props: CartProps)=>{
	var [showing, setShowing] = useState<boolean>(false);
	var [cart, setCart] = useState<CartItem[]>([]);
	var [loading, setLoading] = useState<boolean>(true);

	useEffect(()=>{
		fetchCart();
	}, []);

	useEffect(()=>{
		var handleLogout = ()=>{
			clearRedeemables();
		};
		window.addEventListener('logout', handleLogout);
		return ()=>{
			window.removeEventListener('logout', handleLogout);
		};
	}, []);

	var getSerializedCart = () : SerializedCartItem[]=>{
		var scart = localStorage.getItem('cocoan-cart');
		if(!scart) return [];
		return JSON.parse(scart);
	}

	var saveSerializedCart = (cart: CartItem[])=>{
		var serialized : SerializedCartItem[] = [];
		for(var i of cart){
			serialized.push({
				product_id: i.product_id,
				quantity: i.quantity,
				granel: !!i.price_gram && i.price_gram>0,
				...(i.redeem_points != null ? { redeem_points: i.redeem_points, product_name: i.product_name } : {})
			});
		}
		if(serialized.length>0){
			localStorage.setItem('cocoan-cart', JSON.stringify(serialized))
		}else{
			localStorage.removeItem('cocoan-cart');
		}
	}

	var fetchCart = ()=>{
		var scart = getSerializedCart();
		if(!scart || scart.length===0){
			setLoading(false);
			return;
		}
		var regularScart = scart.filter(a=>a.redeem_points == null);
		var redeemScart = scart.filter(a=>a.redeem_points != null);

		if(regularScart.length === 0){
			setCart(redeemScart.map(s=>({
				product_id: s.product_id,
				product_name: s.product_name || `Producto #${s.product_id}`,
				size: null,
				price: 0,
				quantity: 1,
				redeem_points: s.redeem_points,
			})));
			setLoading(false);
			return;
		}

		API.getCart(regularScart).then(res=>{
			if(res.error){
				setCart([]);
				saveSerializedCart([]);
				return;
			}

			var redeemItems: CartItem[] = redeemScart.map(s=>({
				product_id: s.product_id,
				product_name: s.product_name || `Canje #${s.product_id}`,
				size: null,
				price: 0,
				quantity: 1,
				redeem_points: s.redeem_points,
			}));
			var merged = [...res.data, ...redeemItems];
			saveSerializedCart(merged);
			setCart(merged);
		}).catch(err=>{
			saveSerializedCart([]);
			setCart([]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var addToCart = async (product_id: number, quantity: number, show_cart: boolean=true)=>{
		if(!product_id || !quantity || quantity<=0) return;
		
		var cix = cart.findIndex(a=>a.product_id===product_id);
		if(cix>-1){
			var ct = [...cart];
			ct[cix].quantity += quantity;
			setCart(ct);
			saveSerializedCart(ct);
			if(show_cart) showCart();
			return;
		}
		
		setLoading(true);
		try{
			var res = await API.getCartItem(product_id, quantity)
		}catch(e){
			Toast.error('Hubo un error inesperado agregando el producto al carrito. (LCL-1)');
			return;
		}finally{
			setLoading(false);
		}

		if(res.error){
			Toast.error(res.message);
			return;
		}
		
		var nct = [...cart];
		nct.push(res.data);
		setCart(nct);
		saveSerializedCart(nct);
		if(show_cart) showCart();
	}

	var addRedeemableToCart = (product: { product_id: number, name: string, redeem_points: number })=>{
		var cix = cart.findIndex(a=>a.product_id===product.product_id && a.redeem_points != null);
		if(cix>-1) {
			Toast.error('Este producto ya está en tu carrito para canjear.');
			return;
		}
		var newItem: CartItem = {
			product_id: product.product_id,
			product_name: product.name,
			size: null,
			price: 0,
			quantity: 1,
			redeem_points: product.redeem_points,
		};
		var nct = [...cart, newItem];
		setCart(nct);
		saveSerializedCart(nct);
		showCart();
	}

	var removeFromCart = (product_id: number)=>{

	}

	var clearCart = ()=>{
		setCart([]);
		saveSerializedCart([]);
	}

	var clearRedeemables = ()=>{
		setCart(prev => {
			var filtered = prev.filter(a => a.redeem_points == null);
			saveSerializedCart(filtered);
			return filtered;
		});
	}
	
	var updateCart = async (product_id: number, quantity: number)=>{
		var cix = cart.findIndex(a=>a.product_id===product_id);
		if(cix>-1){
			var ct = [...cart];
			if(quantity<=0){
				ct.splice(cix, 1);
			}else{
				ct[cix].quantity = quantity;
			}
			setCart(ct);
			saveSerializedCart(ct);
			return;
		}
	}

	var getCartItems = ()=>{
		if(cart.length===0) return 0;
		var amt = 0;
		for(var i of cart){
			amt += Math.floor(i.quantity || 1);
		}
		return amt;
	}

	var getTotal = ()=>{
		if(!cart) return 0;
		var amt = 0;
		for(var i of cart){
			amt += (i.price_discounted || i.price) * i.quantity;
		}
		return amt;
	}

	var showCart = ()=>{
		setShowing(true);
	}

	var hideCart = ()=>{
		setShowing(false);
	}

	return (
		<CartContext.Provider value={{
			cart, loading, showing,
			serializedCart: getSerializedCart(),
			addToCart,
			addRedeemableToCart,
			removeFromCart,
			updateCart,
			showCart,
			hideCart,
			clearCart,
			clearRedeemables,
			total: getTotal(),
			items: getCartItems(),
		}}>
			{props.children}
		</CartContext.Provider>
	)
}

var WebdataContext = createContext<WebData>({
	landing_products: null,
	users: null,
	variables: null
});

var useWebdata = () => useContext(WebdataContext);
var WebdataProvider = (props: CartProps)=>{
	var [webdata, setWebdata] = useState<WebData>(null);

	useEffect(()=>{
		if(webdata) return;
		API.getVariables().then(res=>{
			if(res.error) return;
			setWebdata(res.data);
		});
	}, [webdata]);

	return (
		<WebdataContext.Provider value={webdata}>
			{props.children}
		</WebdataContext.Provider>
	)
}

const UserAuthContext = createContext<{
	login: boolean,
	user: UserLogin,
	logout: ()=>void,
	refresh?: ()=>void,
	setUser: React.Dispatch<React.SetStateAction<UserLogin>>,
	token: TokenData,
	setToken: React.Dispatch<React.SetStateAction<TokenData>>
}>({ 
	user: null, 
	token: null,
	login: false, 
	logout: ()=>null, 
	refresh: ()=>null,
	setUser: ()=>null,
	setToken: ()=>null
});
export var useUser = () => useContext(UserAuthContext);

export var UseProvider = (props: PropsWithChildren)=>{
	var [user, setUser] = useState<UserLogin>(JSON.parse(localStorage.getItem('user_data')) || null);
	var [token, setToken] = useState<TokenData>(JSON.parse(localStorage.getItem('access_token')) || null);

	const isExpired = () => {
		const t: TokenData = JSON.parse(localStorage.getItem('access_token'));
		const now = moment().unix();
		return !t || Number(t.expires) <= now + 30;
	 };
	
	var updateData=()=>{
		try {
			var u: UserLogin = JSON.parse(localStorage.getItem('user_data'));
			setUser(u);
			return u;
		} catch (error) {}
	}

	var updateToken=()=>{
		try {
			var t: TokenData = JSON.parse(localStorage.getItem('access_token'));
			setToken(t);
			return t; 
		} catch (error) {}
	}

	var logout=()=>{
		API.logout();
		window.dispatchEvent(new Event('logout'));
	}

	useEffect(()=>{
		window.addEventListener('user', updateData);
		window.addEventListener('token', updateToken);
		return ()=>{
			window.removeEventListener('user', updateData);
			window.removeEventListener('token', updateToken);
		}
	},[])
	
	return <UserAuthContext.Provider value={{
		login: !isExpired() && !!user,
		user: isExpired() ? null : user,
		logout,
		setUser,
		token,
		setToken
	}}>
		{props.children}
	</UserAuthContext.Provider>

}

export function useMediaQuery(query: string){
	const [matches, setMatches] = useState<boolean>(()=>{
		if(typeof window!=='undefined'){
			return window.matchMedia(query).matches;
		}
		return false;
	});

	useEffect(()=>{
		if(typeof window==='undefined')return;

		const mediaQueryList = window.matchMedia(query);
		const listener = (event: MediaQueryListEvent)=>{
			setMatches(event.matches);
		};

		if(mediaQueryList.addEventListener){
			mediaQueryList.addEventListener('change', listener);
		}else{
			(mediaQueryList as any).addListener(listener);
		}

		setMatches(mediaQueryList.matches);

		return ()=>{
			if(mediaQueryList.removeEventListener){
				mediaQueryList.removeEventListener('change', listener);
			}else{
				(mediaQueryList as any).removeListener(listener);
			}
		};
	}, [query]);

	return matches;
}

export {
	TitleProvider,
	useTitle,

	CartProvider,
	useCart,

	WebdataProvider,
	useWebdata,
}